import React from 'react';
import { AddableDropdown } from './Multi/AddableDropdown';
import tw from 'twin.macro';
import { BuiltInProperties } from '$/GraphQL/schema/builtInProperties';
import {
  ActionPropertyDescriptionFragment,
  ActionPropertyType,
  ActionPropertyValueFragment,
} from '$/GraphQL/schema/schema';
import { css } from '@emotion/react';
import { DropdownItemProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem';
import Dropdown from './Basics/Dropdown';
import DangerouslySetInnerHTML from '../EditableInput/DangerouslySetInnerHTML';
import { useTeamPropertiesContext } from '$/components/Property/PropertyProvider';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useFormContext } from 'react-hook-form';
import { convertStringtoPropertyName, generateUniquePropertyName } from '$/utils/string';
import useRunOnce from '$/hooks/useRunOnce';

export const Property: React.FC<{
  o: ActionPropertyDescriptionFragment | ActionPropertyValueFragment;
}> = ({ o }) => {
  const values = React.useMemo(() => {
    if (
      ![
        ActionPropertyType.ExpandableSelection,
        ActionPropertyType.Selection,
        ActionPropertyType.MultiSelection,
        ActionPropertyType.ExpandableMultiSelection,
      ].includes(o.type)
    ) {
      return <></>;
    }
    const values = o.values ?? [];
    const shownValues = values.length <= 6 ? values : [...values.slice(0, 5), '...'];
    return (
      <>
        {shownValues.map((v) => (
          <div key={v} className='value'>
            {v}
          </div>
        ))}
      </>
    );
  }, [o.type, o.values]);
  return (
    <div
      className='flex flex-col justify-start w-full '
      css={[
        tw`text-sm border-b border-gray-200`,
        css`
          a {
            ${tw`text-color-brand`}
            text-decoration: underline;
          }
        `,
      ]}
    >
      <div className='flex justify-between items-baseline'>
        <div className='flex flex-col items-start mb-1/4e'>
          <div className='font-mono font-medium'>{o.property}</div>
        </div>
        <div className='flex flex-col'>
          {o.name !== o.property && <div className='text-lg'>{o.name}</div>}
        </div>
      </div>
      <div className='font-mono -mt-1'>
        {!o.values ? (
          <span className='uppercase'>{o.type}</span>
        ) : (
          <div className='flex font-mono mt-1/2e'>
            <div className='uppercase mr-1e'>{o.type}</div>
            <div className='flex flex-wrap justify-start'>{values}</div>
          </div>
        )}
      </div>
      <DangerouslySetInnerHTML className='mt-1/2e text-gray-600' markdownString={o.description} />
    </div>
  );
};

export const SelectPropertyDropdown: React.FC<{
  className?: string;
  value?: string | string[];
  onChange?: (value: unknown) => void;
  upward?: boolean;
  placeholder?: string;
  excludedFields?: string[];
  sourceFields?:
    | (ActionPropertyDescriptionFragment | ActionPropertyValueFragment)[]
    | ActionPropertyValueFragment[];
  loading?: boolean;
  multiple?: boolean;
}> = ({
  value,
  upward = true,
  className,
  onChange,
  excludedFields,
  sourceFields,
  loading,
  multiple,
  placeholder,
}) => {
  const fields = React.useMemo((): (
    | ActionPropertyDescriptionFragment
    | ActionPropertyValueFragment
  )[] => {
    return sourceFields?.filter((f) => !excludedFields?.includes(f.name)) ?? [];
  }, [sourceFields, excludedFields]);

  const searchFn = React.useCallback(
    (options: (DropdownItemProps & { search: string })[], value: string): DropdownItemProps[] => {
      const words = value.toLowerCase().split(/\s/);
      return options.filter((o) => {
        return words.reduce<boolean>((matches, w) => {
          return matches || o.search.indexOf(`${w}`) !== -1;
        }, false);
      });
    },
    []
  );

  const selectOptions =
    fields
      .sort((a, b) => a.property.localeCompare(b.property))
      .map((o) => ({
        search: ` ${o.property} ${o.name} ${o.type} ${o.description} ${o.values?.join(
          ' '
        )}`.toLowerCase(),
        key: o.property,
        value: o.property,
        text: o.property,
        content: <Property o={o} />,
      })) ?? [];

  return (
    <Dropdown
      lazyLoad
      loading={loading}
      placeholder={
        placeholder ?? (loading ? '...' : !fields ? 'No properties available' : 'Select property')
      }
      selection
      floating
      multiple={multiple}
      options={selectOptions}
      className={`whitespace-nowrap ${className}`}
      value={value}
      onChange={(v: string | string[]): void => {
        onChange && onChange(v);
      }}
      search={searchFn}
      upward={upward}
      css={css`
        > div.text {
          max-height: 1em;
        }
        &.ui.multiple.search.dropdown {
          display: flex;
          flex-wrap: wrap;
          flex-direction: row;
        }
        .menu {
          max-height: 20rem !important;
          min-width: 33rem !important;
          .font-mono {
            ${tw`font-mono`}
          }
          .value {
            ${tw`text-xs rounded-md bg-gray-300 px-2 ml-1/4e mr-1/4e mb-1/2e`};
          }
          .item:nth-of-type(even) {
            ${tw`bg-gray-200 py-1/2e`}
          }
        }
      `}
    />
  );
};

const DefinePropertyDropdown: React.FC<{
  defaultTitle?: string | null;
  defaultProperty?: string | null;
  id?: string;
  name?: string;
  placeholder?: string;
  excludeProperties: string[];
  isCreating?: boolean;
  propertyPrefix?: string;
  onError?: (error: string) => void;
  onModified?: () => void;
}> = ({
  defaultProperty,
  defaultTitle,
  placeholder,
  excludeProperties,
  isCreating,
  propertyPrefix,
  onModified,
  ...props
}) => {
  const [modified, setModified] = React.useState(!isCreating || defaultProperty);
  const [defaultPropertyName, setDefaultPropertyName] = React.useState<string | null | undefined>(
    defaultProperty
  );

  const { setValue } = useFormContext();

  const { properties: allProperties, loading } = useTeamPropertiesContext();

  const generatePropertyName = React.useCallback(
    (shouldDirty?: boolean) => {
      let propertyName = convertStringtoPropertyName(defaultTitle);
      if (!propertyName) {
        setDefaultPropertyName(undefined);
        props?.name && setValue(props.name, undefined, { shouldDirty });
        return;
      }
      // Add prefix to property name with the format: <interaction name>_<card title>
      const originPropertyName = [propertyPrefix, propertyName].join('');
      const existingProperties = excludeProperties
        .map((p) => p?.toLowerCase())
        .filter(Boolean) as string[];
      propertyName = generateUniquePropertyName(originPropertyName, existingProperties);

      setTimeout(() => {
        setDefaultPropertyName(propertyName);
        props?.name && setValue(props.name, propertyName, { shouldDirty });
      }, 0);
    },
    [defaultTitle, excludeProperties, propertyPrefix, props.name, setValue]
  );

  useRunOnce(() => {
    // If the default property name is set, we don't need to generate a new one
    if (!modified || (modified && defaultProperty)) return;
    // Generate a property name from the card headline when opening interaction with an empty property name
    generatePropertyName(true);
    setModified(true);
  });

  const titleChanged = useHasChangedShallow([defaultTitle, excludeProperties]);
  React.useEffect(() => {
    if (!titleChanged || modified) return;
    generatePropertyName();
  }, [modified, generatePropertyName, titleChanged]);

  const handleModified = React.useCallback(() => {
    setModified(true);
    onModified && onModified();

    // Callers need to useCallback onModified but they don't
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectOptions = React.useMemo(() => {
    const excludedPropertiesSet = new Set(
      (excludeProperties ?? []).concat(BuiltInProperties).map((p) => p.toLowerCase())
    );

    let properties = (allProperties ?? [])
      .filter(
        (p) =>
          p.source !== 'builtin' &&
          !p.parentProperty &&
          !excludedPropertiesSet.has(p.property.toLowerCase())
      )
      .map((p) => p.property);

    // Add default propertyName to list of properties
    if (defaultPropertyName && !properties.includes(defaultPropertyName)) {
      properties = [defaultPropertyName].concat(properties);
    }

    return properties
      ?.map((f) => {
        return { key: f, text: f, value: f };
      })
      .sort((a, b) => a.text.localeCompare(b.text));
  }, [allProperties, defaultPropertyName, excludeProperties]);

  const formatNewValue = React.useCallback(
    (value: string): string => {
      const newValue = value.replace(/\s/g, '_');

      if (
        allProperties
          ?.filter((p) => p.source === 'builtin' || p.parentProperty)
          .find((p) => p.property === newValue.toLowerCase())
      ) {
        const errorMsg = `${newValue} is already in use by a built-in property.`;
        props.onError && props.onError(errorMsg);
        throw new Error(errorMsg);
      }
      return newValue;
    },
    [allProperties, props]
  );

  // TODO: We need to start policing property types.  If we are setting a property
  // already defined it should be to a field that's of equivalent type.
  // i.e. Setting a Poll property to a Text interaction or reg form field means
  // that field is no longer purely a SELECTION

  return (
    <AddableDropdown
      {...props}
      onModified={handleModified}
      clearable
      selectOnBlur={false}
      loading={loading}
      placeholder={placeholder || 'Select property'}
      formatNewValue={formatNewValue}
      options={selectOptions ?? []}
      css={css`
        .menu {
          max-height: 20rem !important;
        }
        input,
        .divider.text,
        .item {
          ${tw`font-code!`}
        }
      `}
    />
  );
};

export default DefinePropertyDropdown;
